import { useState, useEffect, useCallback } from 'react';
import { SummaryLevel } from '@/ui';

export interface ImageData {
  id: string;
  url: string;
  alt: string;
  sourceUrl: string;
  title?: string;
  description?: string;
}

export interface HistoryItem {
  id: string;
  timestamp: Date;
  content: string;
  url?: string;
  fileName?: string;
  mode: 'text' | 'url' | 'file';
  level: SummaryLevel['type'];
  result: {
    summary: string;
    originalLength: number;
    summaryLength: number;
    compressionRatio: number;
    timeToReadOriginal?: number;
    timeToReadSummary?: number;
    timeSaved?: number;
    timeSavedPercentage?: number;
  };
  // Images associées (pour les URLs)
  images?: ImageData[];
  // Hash pour déduplication
  contentHash: string;
}

const STORAGE_KEY = 'abregecontenu_history_cache';
const MAX_ITEMS = 20;
const MAX_AGE_DAYS = 30;

// Fonction sécurisée pour encoder les chaînes Unicode
const safeBase64Encode = (str: string): string => {
  try {
    // Utilise encodeURIComponent pour gérer les caractères Unicode, puis btoa
    return btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
      })
    );
  } catch (error) {
    // Fallback: utilise un hash simple basé sur les caractères
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convertit en entier 32-bit
    }
    return Math.abs(hash).toString(36).slice(0, 12);
  }
};

// Génère un hash simple pour déduplication
const generateContentHash = (
  content: string,
  url?: string,
  fileName?: string,
  level?: string
): string => {
  // Pour les URLs, utiliser l'URL comme identifiant principal
  // Pour les fichiers, utiliser le nom de fichier
  // Pour le texte, utiliser le contenu
  let identifier = content;
  if (url) {
    identifier = url; // L'URL est l'identifiant unique pour les URLs
  } else if (fileName) {
    identifier = fileName; // Le nom de fichier est l'identifiant unique pour les fichiers
  }

  const combined = `${identifier}-${level || ''}`;
  return safeBase64Encode(combined).slice(0, 12);
};

// Nettoie les éléments trop anciens
const cleanOldItems = (items: HistoryItem[]): HistoryItem[] => {
  const maxAge = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  return items
    .filter((item) => new Date(item.timestamp).getTime() > maxAge)
    .slice(-MAX_ITEMS); // Garde seulement les 20 plus récents
};

export const useHistoryCache = () => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Charger l'historique depuis localStorage
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(STORAGE_KEY);
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        // Vérifier que parsed est un tableau
        if (Array.isArray(parsed)) {
          const cleanItems = cleanOldItems(
            parsed.map((item: any) => ({
              ...item,
              timestamp: new Date(item.timestamp),
            }))
          );
          setHistory(cleanItems);
        } else {
          console.warn("Format d'historique invalide, réinitialisation...");
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'historique:", error);
      // En cas d'erreur, nettoyer le localStorage corrompu
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (cleanupError) {
        console.error('Impossible de nettoyer le localStorage:', cleanupError);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Sauvegarder dans localStorage
  const saveHistory = useCallback((items: HistoryItem[]) => {
    try {
      // Vérifier que localStorage est disponible
      if (typeof Storage === 'undefined') {
        console.warn('localStorage non disponible');
        return;
      }

      // Tenter de sauvegarder
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'historique:", error);

      // Si l'erreur est due à un quota dépassé, essayer de nettoyer l'ancien historique
      if (error instanceof DOMException && error.code === 22) {
        console.warn(
          "Quota localStorage dépassé, nettoyage de l'ancien historique..."
        );
        try {
          // Garder seulement les 10 éléments les plus récents
          const reducedItems = items.slice(-10);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(reducedItems));
        } catch (retryError) {
          console.error(
            'Impossible de sauvegarder même avec un historique réduit:',
            retryError
          );
        }
      }
    }
  }, []);

  // Chercher dans le cache - ne retourne un résultat que si c'est le même type de résumé
  // Comportement: Si vous avez résumé un texte en "points clés", le cache ne retournera
  // ce résultat que si vous demandez à nouveau "points clés". Si vous demandez "détaillé",
  // il ne trouvera rien dans le cache et fera un nouvel appel API.
  const findInCache = useCallback(
    (
      content: string,
      url?: string,
      fileName?: string,
      level?: SummaryLevel['type']
    ): HistoryItem | null => {
      // Trouve l'élément avec le même identifiant et le même niveau
      return (
        history.find((item) => {
          // Vérifier que c'est le même type de contenu
          let sameIdentifier = false;

          if (url && item.url) {
            // Pour les URLs, comparer les URLs
            sameIdentifier = item.url === url;
          } else if (fileName && item.fileName) {
            // Pour les fichiers, comparer les noms de fichiers
            sameIdentifier = item.fileName === fileName;
          } else if (!url && !fileName && !item.url && !item.fileName) {
            // Pour le texte, comparer le contenu
            sameIdentifier = item.content === content;
          }

          // Vérifier que c'est le même niveau
          const sameLevel = item.level === level;

          return sameIdentifier && sameLevel;
        }) || null
      );
    },
    [history]
  );

  // Ajouter un nouvel élément
  const addToHistory = useCallback(
    (
      content: string,
      mode: 'text' | 'url' | 'file',
      level: SummaryLevel['type'],
      result: HistoryItem['result'],
      url?: string,
      fileName?: string,
      images?: ImageData[]
    ) => {
      const contentHash = generateContentHash(content, url, fileName, level);

      const newItem: HistoryItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date(),
        content,
        url,
        fileName,
        mode,
        level,
        result,
        images: images || [],
        contentHash,
      };

      setHistory((prevHistory) => {
        // Cherche s'il existe déjà un élément avec le même contenu ET le même type de résumé
        const existingIndex = prevHistory.findIndex(
          (item) => item.contentHash === contentHash
        );

        let updatedHistory: HistoryItem[];

        if (existingIndex >= 0) {
          // Remplacer l'élément existant et le remonter en tête
          updatedHistory = [
            newItem,
            ...prevHistory.filter((_, index) => index !== existingIndex),
          ];
        } else {
          // Ajouter le nouvel élément en tête
          updatedHistory = [newItem, ...prevHistory];
        }

        // Nettoyer et limiter
        const cleanItems = cleanOldItems(updatedHistory);
        saveHistory(cleanItems);
        return cleanItems;
      });

      return newItem;
    },
    [saveHistory]
  );

  // Supprimer un élément
  const removeFromHistory = useCallback(
    (id: string) => {
      setHistory((prevHistory) => {
        const updatedHistory = prevHistory.filter((item) => item.id !== id);
        saveHistory(updatedHistory);
        return updatedHistory;
      });
    },
    [saveHistory]
  );

  // Vider l'historique
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Vérifier si un contenu a déjà été résumé avec un type spécifique
  const hasSummaryForLevel = useCallback(
    (
      content: string,
      level: SummaryLevel['type'],
      url?: string,
      fileName?: string
    ): boolean => {
      return findInCache(content, url, fileName, level) !== null;
    },
    [findInCache]
  );

  // Obtenir tous les types de résumé disponibles pour un contenu donné
  const getAvailableSummaryLevels = useCallback(
    (
      content: string,
      url?: string,
      fileName?: string
    ): SummaryLevel['type'][] => {
      const matchingItems = history.filter((item) => {
        // Vérifier que c'est le même type de contenu
        let sameIdentifier = false;

        if (url && item.url) {
          // Pour les URLs, comparer les URLs
          sameIdentifier = item.url === url;
        } else if (fileName && item.fileName) {
          // Pour les fichiers, comparer les noms de fichiers
          sameIdentifier = item.fileName === fileName;
        } else if (!url && !fileName && !item.url && !item.fileName) {
          // Pour le texte, comparer le contenu
          sameIdentifier = item.content === content;
        }

        return sameIdentifier;
      });

      return matchingItems.map((item) => item.level);
    },
    [history]
  );

  // Statistiques
  const stats = {
    totalItems: history.length,
    totalTimeSaved: history.reduce(
      (acc, item) => acc + (item.result.timeSaved || 0),
      0
    ),
    averageCompression:
      history.length > 0
        ? history.reduce((acc, item) => acc + item.result.compressionRatio, 0) /
          history.length
        : 0,
  };

  return {
    history,
    isLoading,
    findInCache,
    addToHistory,
    removeFromHistory,
    clearHistory,
    hasSummaryForLevel,
    getAvailableSummaryLevels,
    stats,
  };
};
